/**
 * Column sorting, derived rather than declared.
 *
 * No admin table had sorting at all, and the app has roughly two hundred columns across its
 * list pages — annotating each by hand would have been a long diff that still went stale the
 * moment someone added a column. Instead a sorter is derived from the field's name and a
 * sample of the data, so a column added tomorrow is sortable tomorrow.
 *
 * A column opts out with `sortAs: false`, or pins the comparison with
 * `sortAs: "number" | "date" | "text"`. An explicit `sorter` always wins.
 */

export type SortKind = "number" | "date" | "text";

export type SortableColumn = {
  dataIndex?: any;
  key?: any;
  sorter?: any;
  sortAs?: SortKind | false;
  [key: string]: any;
};

/**
 * Dates are recognised by name rather than by parsing, because `Date.parse` happily accepts
 * things that are not dates — a reference like "2024" would sort as a year.
 */
export const inferSortKind = (column: SortableColumn, rows: any[]): SortKind => {
  const field = String(column.dataIndex ?? "");
  if (/date|time|^.*at$/i.test(field)) return "date";

  const sample = rows.find((row) => row?.[field] !== null && row?.[field] !== undefined)?.[field];
  if (typeof sample === "number") return "number";
  if (typeof sample === "string" && sample.trim() !== "" && !Number.isNaN(Number(sample))) return "number";
  return "text";
};

export const buildSorter = (field: string, kind: SortKind) => (a: any, b: any) => {
  const left = a?.[field];
  const right = b?.[field];

  // Blanks sort last in both directions rather than posing as zero or the epoch, which would
  // bury real rows under empty ones whenever a column is sparsely filled.
  const leftEmpty = left === null || left === undefined || left === "";
  const rightEmpty = right === null || right === undefined || right === "";
  if (leftEmpty && rightEmpty) return 0;
  if (leftEmpty) return 1;
  if (rightEmpty) return -1;

  if (kind === "number") return (Number(left) || 0) - (Number(right) || 0);
  if (kind === "date") return new Date(left).getTime() - new Date(right).getTime();
  // `numeric` so "item 2" precedes "item 10"; `base` so case and accents do not split groups.
  return String(left).localeCompare(String(right), undefined, {numeric: true, sensitivity: "base"});
};

/** Adds a derived sorter to every column that has a field and has not opted out. */
export function withSorters<T extends SortableColumn>(columns: T[], rows: any[]): T[] {
  return (columns || []).map((column) => {
    const field = column?.dataIndex;
    if (!field || column.sorter !== undefined || column.sortAs === false) return column;

    // `sortAs: false` already returned above, so anything truthy here names the comparison.
    const kind: SortKind = column.sortAs ? column.sortAs : inferSortKind(column, rows);
    return {...column, sorter: buildSorter(String(field), kind), showSorterTooltip: false};
  });
}
